import React from 'react';
import { extractHeaderLabels, applyCellLabels } from '../tableLabels';
import { useUserPrefs } from '../../hooks/useUserPrefs';

export type MaintenanceLayoutMode = 'classic' | 'service' | 'directory';

const MaintenanceViewContext = React.createContext<MaintenanceLayoutMode>('classic');

export function useMaintenanceView() {
  return React.useContext(MaintenanceViewContext);
}

/** Every Maintenance hub opens in the Directory layout unless the person chose another one. */
export const MAINTENANCE_DEFAULT_LAYOUT: MaintenanceLayoutMode = 'directory';

export function normalizeMaintenanceLayout(value: unknown, fallback: MaintenanceLayoutMode = MAINTENANCE_DEFAULT_LAYOUT): MaintenanceLayoutMode {
  return value === 'classic' || value === 'service' || value === 'directory' ? value : fallback;
}

/**
 * The ui_prefs path of a hub's layout choice. A fresh namespace (2026-10-01): until then each hub
 * opened in Service and wrote that default back on mount, so the old
 * `admin.<hub>.maintenanceLayoutMode` values were mostly saved defaults, not choices.
 */
export function maintenanceLayoutPrefKey(hubKey: string): string {
  return `maintenanceLayouts.${hubKey}`;
}

type LayoutPrefsController = {
  get: <T>(path: string, fallback: T) => T;
  set: (path: string, value: unknown) => void;
};

/**
 * A hub's layout: Directory by default, saved only when the person picks one. Pass the page's
 * own `useUserPrefs()` result to avoid a second preferences read.
 */
export function useMaintenanceLayoutPreference(hubKey: string, prefs?: LayoutPrefsController) {
  const own = useUserPrefs(undefined, !prefs);
  const get = prefs?.get ?? own.get;
  const set = prefs?.set ?? own.set;
  const key = maintenanceLayoutPrefKey(hubKey);
  const mode = normalizeMaintenanceLayout(get(key, MAINTENANCE_DEFAULT_LAYOUT));
  const setMode = React.useCallback((next: MaintenanceLayoutMode) => set(key, next), [key, set]);
  return [mode, setMode] as const;
}

/** Keeps each page's presentation local, including when a manager is embedded in another page. */
export function MaintenanceView({ mode, children }: { mode: MaintenanceLayoutMode; children: React.ReactNode }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const root = rootRef.current;
    if (mode !== 'directory' || !root) return;
    // Match CardableTable's responsive labels, including rows loaded after the shell.
    const labelTables = () => root.querySelectorAll<HTMLTableElement>('table.data-table').forEach((table) => {
      applyCellLabels(table, extractHeaderLabels(table));
    });
    labelTables();
    const observer = new MutationObserver(labelTables);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [mode]);
  return (
    <MaintenanceViewContext.Provider value={mode}>
      <div ref={rootRef} className={`maintenance-view${mode === 'directory' ? ' maintenance-directory-view' : ''}`} data-maintenance-layout={mode}>
        {children}
      </div>
    </MaintenanceViewContext.Provider>
  );
}

export function MaintenanceLayoutToggle({ value, onChange, label }: {
  value: MaintenanceLayoutMode;
  onChange: (mode: MaintenanceLayoutMode) => void;
  label: string;
}) {
  return (
    <div className="items-maintenance-layout-toggle" role="group" aria-label={label}>
      <button type="button" aria-label="Use classic layout" aria-pressed={value === 'classic'} className={value === 'classic' ? 'is-active' : undefined} onClick={() => onChange('classic')}>Classic</button>
      <button type="button" aria-label="Use service layout" aria-pressed={value === 'service'} className={value === 'service' ? 'is-active' : undefined} onClick={() => onChange('service')}>Service</button>
      <button type="button" aria-label="Use directory layout" aria-pressed={value === 'directory'} className={value === 'directory' ? 'is-active' : undefined} onClick={() => onChange('directory')}>Directory</button>
    </div>
  );
}

export function revealMaintenanceSection(element: Element | null | undefined) {
  const disclosure = element?.closest('details');
  if (disclosure) disclosure.open = true;
}

/**
 * Opens the section's disclosure and scrolls it in under any docked header (its scroll-margin-top), then
 * aligns it once more when the smooth scroll settles: the phone top bar collapses while the page scrolls,
 * which moves the content after the scroll's destination was fixed. Returns a cleanup for effects.
 */
export function scrollToMaintenanceSection(element: Element | null | undefined): () => void {
  if (!element) return () => {};
  revealMaintenanceSection(element);
  element.scrollIntoView();
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    window.removeEventListener('scrollend', settle);
    window.clearTimeout(fallback);
    element.scrollIntoView({ behavior: 'instant' });
  };
  window.addEventListener('scrollend', settle);
  // No scrollend when nothing scrolled, or in browsers without the event.
  const fallback = window.setTimeout(settle, 1200);
  return () => {
    settled = true;
    window.removeEventListener('scrollend', settle);
    window.clearTimeout(fallback);
  };
}

export function MaintenanceDirectoryDisclosure({ title, description, children, open, onToggle, revealKey }: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  revealKey?: string | null;
  onToggle?: (open: boolean) => void;
}) {
  const mode = useMaintenanceView();
  const disclosureRef = React.useRef<HTMLDetailsElement>(null);
  React.useEffect(() => {
    if (mode !== 'directory' || !open || revealKey === undefined) return;
    const frame = requestAnimationFrame(() => {
      disclosureRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      disclosureRef.current?.querySelector('summary')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [mode, open, revealKey]);
  if (mode !== 'directory') return <>{children}</>;
  return (
    <details ref={disclosureRef} className="maintenance-directory-disclosure" open={open} onToggle={onToggle ? (event) => onToggle(event.currentTarget.open) : undefined}>
      <summary>
        <span><strong>{title}</strong>{description ? <span className="maintenance-directory-disclosure__description">{description}</span> : null}</span>
        <span className="maintenance-directory-disclosure__action" aria-hidden="true">Manage <span>›</span></span>
      </summary>
      <div className="maintenance-directory-disclosure__content">{children}</div>
    </details>
  );
}
